import React, { createContext, useContext, useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import styled from 'styled-components';
import { easing } from './lib/easing';

/**
 * Radio Context
 */

const Context = createContext({});

/**
 * Radio
 */

const HiddenInput = styled.input`
  cursor: inherit;
  height: 100%;
  left: 0;
  margin: 0;
  opacity: 0;
  padding: 0;
  position: absolute;
  top: 0;
  width: 100%;
  z-index: 1;
`;

const Inner = styled.div`
  background-color: var(--radio);
  border-radius: 4px;
  bottom: 0;
  content: '';
  display: block;
  height: 8px;
  left: 2px;
  position: absolute;
  right: 0;
  top: 2px;
  transform: scale(0);
  transition: ${easing('background-color', 'transform')};
  width: 8px;
`;

const Outer = styled.div`
  align-items: center;
  background-color: white;
  border-radius: 8px;
  border: 2px solid var(--radio);
  box-shadow: 0 0 0 0 transparent;
  color: white;
  display: flex;
  flex-shrink: 0;
  font-size: var(--xx-small);
  height: 16px;
  justify-content: center;
  position: relative;
  text-align: center;
  transition: ${easing('background-color', 'border-color', 'box-shadow')};
  width: 16px;
`;

const Label = styled.span`
  font-weight: var(--semi-bold);
`;

const RadioWrapper = styled.label`
  align-items: flex-start;
  display: flex;
  cursor: pointer;
  position: relative;

  ${Outer} {
    margin-right: 0.8em;
  }

  ${HiddenInput}:focus + ${Outer} {
    background: var(--radio-selected-focus-bg);
    box-shadow: 0 0 0 5px var(--radio-selected-focus-bg);
    outline: none;
  }

  ${({ checked, disabled }) => {
    switch (true) {
      case !checked && !disabled:
        return `
          &:hover,
          &:focus-within {
            ${Outer} {
              border-color: var(--radio-hover);
            }

            ${Inner} {
              background-color: var(--radio-hover);
            }
          }
        `;

      case checked && !disabled:
        return `
          ${Outer} {
            border-color: var(--radio-checked);
          }

          ${Inner} {
            background-color: var(--radio-checked);
            transform: scale(1);
          }

          &:hover,
          &:focus-within {
            ${Outer} {
              border-color: var(--radio-checked-hover);
            }

            ${Inner} {
              background-color: var(--radio-checked-hover);
            }
          }
        `;

      case !checked && disabled:
        return `
          cursor: default;

          ${Outer}, ${Label} {
            border-color: var(--radio-disabled);
            color: var(--radio-disabled);
          }

          ${Inner} {
            background-color: var(--radio-disabled);
          }
        `;

      default:
      case checked && disabled:
        return `
          cursor: default;

          ${Outer}, ${Label} {
            border-color: var(--radio-disabled);
            color: var(--radio-disabled);
          }

          ${Inner} {
            background-color: var(--radio-disabled);
          }
        `;
    }
  }}
`;

function Radio({ children, className, disabled, label, name, value }) {
  const { onChange, selected } = useContext(Context);

  const checked = value === selected;

  /*
   * NOTE: Due to a bug in React, the <input> onChange handler will still be
   *       called even after default has been prevented for the click handler.
   *       To workaround the double change handler, we check whether the native
   *       event was default prevented and prevent default after this
   *       short-circuit. [https://github.com/facebook/react/issues/9023]
   */
  const handleChange = event => {
    if (disabled || event.nativeEvent.defaultPrevented) {
      return;
    }

    event.preventDefault();

    if (onChange) {
      event.persist();
      onChange(value, event);
    }
  };

  return (
    <RadioWrapper
      checked={checked}
      className={className}
      disabled={disabled}
      htmlFor={name}
      onClick={handleChange}
    >
      <HiddenInput
        aria-checked={checked}
        aria-disabled={disabled}
        aria-label={label}
        checked={checked}
        disabled={disabled}
        name={name}
        onChange={handleChange}
        role="radio"
        tabIndex={!checked ? '-1' : '0'}
        type="radio"
        value={value}
      />

      <Outer>
        <Inner />
      </Outer>

      {(children || label) && <Label>{children || label}</Label>}
    </RadioWrapper>
  );
}

Radio.propTypes = {
  children: PropTypes.node,
  className: PropTypes.string,
  disabled: PropTypes.bool,
  label: PropTypes.string,
  name: PropTypes.string,
  value: PropTypes.string,
};

export default styled(Radio)``;

/**
 * RadioGroup
 */

const RadioGroupWrapper = styled.div`
  ${RadioWrapper} {
    min-height: 40px;
    padding: 12px 24px;

    &:hover {
      background-color: var(--radio-group-hover);
      border-radius: 6px;
    }
  }
`;

function BaseRadioGroup({
  children,
  className,
  onChange,
  value: initialValue,
}) {
  const [selected, setSelected] = useState(initialValue);

  useEffect(() => {
    setSelected(initialValue);
  }, [initialValue]);

  const handleChange = (value, event) => {
    if (value === selected) {
      return;
    }

    setSelected(value);

    if (onChange) {
      onChange(value, event);
    }
  };

  const context = { onChange: handleChange, selected };

  return (
    <Context.Provider value={context}>
      <RadioGroupWrapper className={className} role="radiogroup">
        {children}
      </RadioGroupWrapper>
    </Context.Provider>
  );
}

BaseRadioGroup.propTypes = {
  children: PropTypes.node,
  className: PropTypes.string,
  onChange: PropTypes.func,
  value: PropTypes.string,
};

export const RadioGroup = styled(BaseRadioGroup)``;
